import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  [
    'flex items-center flex-row justify-center gap-2 justify-center text-sm font-normal transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
    'ea-button',
  ],
  {
    variants: {
      variant: {
        /*
         * Every coloured variant uses `walnut` (#8B6B4A, tailwind.config.js) — filled, outline or the
         * opaque `walnut-soft` tint — so no button carries the CMS `primary`, the old sienna or a one-off colour.
         * Exceptions on purpose: `destructive*` stays red (removing things), `white` sits on
         * photos, `none` is unstyled.
         */
        default: ['bg-walnut text-white hover:bg-walnut/90', 'ea-button--default'],
        'primary-foreground': [
          'bg-walnut-soft text-walnut hover:bg-walnut hover:text-white',
          'ea-button--primary-foreground',
        ],
        destructive: [
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
          'ea-button--destructive',
        ],
        accent: ['bg-walnut text-white shadow-sm hover:bg-walnut/90', 'ea-button--accent'],
        outline: [
          'border border-walnut bg-transparent text-walnut hover:bg-walnut/10',
          'ea-button--outline',
        ],
        secondary: [
          'bg-walnut-soft text-walnut hover:bg-walnut hover:text-white',
          'ea-button--secondary',
        ],
        ghost: ['text-muted bg-transparent hover:bg-walnut/10', 'ea-button--ghost'],
        link: ['text-walnut underline-offset-4 hover:underline', 'ea-button--link'],
        dark: ['border border-walnut bg-walnut text-white hover:bg-walnut/90', 'ea-button--dark'],
        /** Figma's Button text style is Montserrat SemiBold; the shared base is `font-normal`. */
        brand: ['bg-walnut text-white font-semibold hover:bg-walnut/90', 'ea-button--brand'],
        /** Outline on a dark surface (hero photo): walnut border, white label, fills on hover. */
        'brand-outline': [
          'border border-walnut bg-transparent text-white font-semibold hover:bg-walnut',
          'ea-button--brand-outline',
        ],
        /** Outline on a light surface (product-card CTA). */
        'brand-bordered': [
          'border border-walnut bg-transparent text-walnut font-bold hover:bg-walnut/10',
          'ea-button--brand-bordered',
        ],
        /** Filled, lighter label: the category filter buttons. */
        walnut: ['bg-walnut text-[#FAF8F3] font-medium hover:bg-walnut/90', 'ea-button--walnut'],
        rating: ['text-rating bg-rating/20 hover:bg-rating/25'],
        white: ['text-black bg-white hover:bg-white/90'],
        none: ['bg-transparent p-0 m-0 shadow-none hover:bg-transparent', 'ea-button--none'],
        'default-alt': ['text-walnut bg-walnut/20 hover:bg-walnut/25'],
        'destructive-alt': ['text-destructive bg-destructive/20 hover:bg-destructive/25'],
        'accent-alt': ['text-walnut bg-walnut/20 hover:bg-walnut/25'],
      },
      rounded: {
        none: ['rounded-none', 'ea-button--rounded-none'],
        default: ['rounded-[4px]', 'ea-button--rounded-default'],
      },
      size: {
        none: ['p-0 m-0 w-fit h-fit', 'ea-button--size-none'],
        default: ['h-12 px-4 sm:px-5 lg:px-6', 'ea-button--size-default'],
        xs: ['h-8 px-3 text-xs', 'ea-button--size-xs'],
        sm: ['h-10 px-6 text-sm', 'ea-button--size-sm'],
        /**
         * Figma product-card CTA: 40px tall, 12px horizontal padding. `min-h` instead of a fixed
         * height: in the 2-column mobile grid the ~140px card is narrower than "Zobrazit produkt",
         * and a fixed `h-10` squeezed the wrapped label edge to edge.
         */
        card: [
          'min-h-10 h-auto px-3 py-2 text-center text-sm leading-tight',
          'ea-button--size-card',
        ],
        lg: ['h-14 px-8', 'ea-button--size-lg'],
        'icon-md': ['min-w-10 max-w-10 min-h-10 max-h-10', 'ea-button--size-icon-md'],
        icon: ['min-w-12 max-w-12 max-h-12 min-h-12', 'ea-button--size-icon'],
        'icon-sm': ['h-7 w-7', 'ea-button--size-icon-sm'],
        'icon-xs': ['h-5 w-5', 'ea-button--size-icon-xs'],
        'icon-scaling': [
          'min-w-6 max-w-6 min-h-6 max-h-6 sm:min-h-8 sm:max-h-8 sm:min-w-8 sm:max-w-8 lg:min-h-10 lg:max-h-10 lg:min-w-10 lg:max-w-10',
          'ea-button--size-icon-sm',
        ],
        'icon-lg': [
          'min-w-8 max-w-8 min-h-8 max-h-8 sm:min-w-10 sm:max-w-10 sm:min-h-10 sm:max-h-10 lg:min-w-12 lg:max-w-12 lg:min-h-12 lg:max-h-12',
          'ea-button--size-icon-lg',
        ],
        'icon-xl': [
          'max-h-[52px] max-w-[52px] min-h-[52px] min-w-[52px]',
          'ea-button--size-icon-xl',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
      rounded: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, isLoading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const blocked = Boolean(props.disabled || isLoading);

    // Keep the original DOM/content in flow: no wrapper may change flex gaps,
    // responsive labels, intrinsic width, or the accessible name during loading.
    const loadingContent = (label: React.ReactNode) => (
      <>
        {label}

        {isLoading && (
          <span
            data-ea-button-spinner=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{ margin: 0 }}
          >
            <Spinner />
          </span>
        )}
      </>
    );
    const content =
      asChild && React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)
        ? React.cloneElement(children, {
            children: loadingContent(children.props.children),
            ...(isLoading && {
              style: { ...children.props.style, WebkitTextFillColor: 'transparent' },
            }),
            // Native disabled does not disable links. Inert blocks pointer and
            // keyboard activation without adding client-only event handlers.
            ...(blocked && {
              tabIndex: -1,
              inert: true,
            }),
          })
        : asChild
          ? children
          : loadingContent(children);
    return (
      <Comp
        className={cn(
          isLoading && 'relative [&>*:not([data-ea-button-spinner])]:opacity-0',
          cn(buttonVariants({ variant, size, rounded, className })),
        )}
        ref={ref}
        {...props}
        style={isLoading ? { ...props.style, WebkitTextFillColor: 'transparent' } : props.style}
        disabled={blocked}
        aria-disabled={blocked}
        aria-busy={isLoading || props['aria-busy']}
        data-ea-button-loading={isLoading || undefined}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
